/**
 * React Query hook for managing Deck Column data at the Deck level.
 * This hook provides a clean interface for fetching, caching, and managing deck columns using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import { DeckColumn, GetDeckColumnForDeckQueryVariables } from '@services/api/deckManagement/graphql/schema/API';
import { getDeckColumnForDeck } from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { createQueryOrMutationKey, createStandardQueryOptions, StandardQueryOptions } from '@utils/reactQueryConfig.ts';

/**
 * Interface for GetDeckColumnForDeck operation parameters
 * @interface UseGetDeckColumnForDeckParams
 * @property {string} deck_id - The unique identifier for the deck
 * @property {string} [publish_timestamp] - Optional timestamp for published data
 * @property {number} [limit] - Optional limit for pagination
 * @property {string} [nextToken] - Optional token for pagination
 * @property {StandardQueryOptions} [options] - Additional React Query options
 */
export interface UseGetDeckColumnForDeckParams extends GetDeckColumnForDeckQueryVariables {
  options?: StandardQueryOptions;
}

export const deckColumnQueryKeys = {
  all: createQueryOrMutationKey('deckColumn'),
  forDeck: (deckId: string) => createQueryOrMutationKey(...deckColumnQueryKeys.all, 'columns', 'forDeck', deckId),
};

/**
 * Hook to fetch deck columns for a specific deck
 * @param {UseGetDeckColumnForDeckParams} params - Parameters for fetching deck columns
 * @param {string} params.deck_id - The unique identifier for the deck
 * @param {string} [params.publish_timestamp] - Optional timestamp for published data
 * @param {number} [params.limit] - Optional limit for pagination
 * @param {StandardQueryOptions} [params.options] - Additional React Query options
 * @returns {UseQueryResult<Array<DeckColumn>>} Query result containing deck columns array
 */
export const useGetDeckColumnForDeck = ({
  deck_id,
  publish_timestamp,
  limit,
  options,
}: UseGetDeckColumnForDeckParams): UseQueryResult<Array<DeckColumn>> => {
  const { deckManagementClient } = useApiClients();

  const { enabled: enabledOption, ...restOptions } = options || {};
  const isEnabled = !!deck_id && (enabledOption ?? true);
  return useQuery({
    queryKey: deckColumnQueryKeys.forDeck(deck_id),
    queryFn: async (): Promise<Array<DeckColumn>> => {
      if (!isEnabled) {
        console.debug('Query useGetDeckColumnForDeck disabled, returning empty array');
        return [];
      }

      let allItems: DeckColumn[] = [];
      let currentNextToken: string | null | undefined = null;

      do {
        const variables: GetDeckColumnForDeckQueryVariables = {
          deck_id,
          publish_timestamp,
          limit,
          nextToken: currentNextToken,
        };

        const response = await deckManagementClient.graphql({
          query: getDeckColumnForDeck,
          variables,
        });

        if ('data' in response && response.data) {
          const paginatedResponse = response.data.getDeckColumnForDeck;
          const items = paginatedResponse?.items?.filter((item): item is DeckColumn => item !== null) ?? [];
          allItems = [...allItems, ...items];
          currentNextToken = paginatedResponse?.nextToken;
        } else {
          break;
        }
      } while (currentNextToken);

      return allItems as Array<DeckColumn>;
    },
    ...createStandardQueryOptions<Array<DeckColumn>>(restOptions),
    enabled: isEnabled,
  });
};
